import React, { useState, useEffect, useMemo } from 'react';
import {
  IcSparkles,
  IcChevronLeft,
  IcChevronRight,
  IcSearch,
  IcClose,
  IcUser,
  IcBanknote,
  IcCheckCircle,
  IcAlertCircle,
} from '../../theme/icons';
import VenteTabs from '../../components/VenteTabs';
import salesService from '../../services/salesService';
import { matchesSearch } from '../../utils/search';

// « Historique service » (mobile) — soins / services vendus. Paginates BY DAY like
// the sales history, but flattens each sale into its SERVICE lines only.
const DAY_PAGE_SIZE = 1000;

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmt = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;

export default function MobileServiceHistory() {
  const [currentDay, setCurrentDay] = useState(fmtDateInput(new Date()));
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await salesService.getSalesPage({
          startDate: currentDay, endDate: currentDay, page: 1, pageSize: DAY_PAGE_SIZE,
        });
        if (active) setSales(res.results || []);
      } catch (err) {
        if (active) { console.error('Error fetching service history:', err); setError('Erreur lors du chargement.'); setSales([]); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [currentDay]);

  // Flatten every sale into one row per SERVICE line.
  const rows = useMemo(() => {
    const out = [];
    sales.forEach((sale) => {
      const client = sale.customer
        ? `${sale.customer.first_name || ''} ${sale.customer.last_name || ''}`.trim() || sale.customer_name
        : (sale.customer_name || 'Client de passage');
      (sale.service_details || []).forEach((s, i) => {
        const price = parseFloat(s.price) || 0;
        const remise = parseFloat(s.remise) || 0;
        const fp = remise > 0 ? price * (1 - remise / 100) : price;
        const total = s.total_detail != null ? Number(s.total_detail) : fp * (s.quantity || 1);
        out.push({
          key: `${sale.id}-s${i}`,
          saleId: sale.id,
          name: s.service_name || s.service?.name || 'Service',
          quantity: s.quantity || 1,
          total,
          client,
          paid: Number(sale.credit || 0) <= 0,
          date: sale.date_added,
        });
      });
    });
    // Règle Ki Para (utils/search.js) : début de mot, sans accents.
    return out.filter((r) => matchesSearch(search, [r.name, r.client]));
  }, [sales, search]);

  const totals = useMemo(() => rows.reduce(
    (acc, r) => ({ count: acc.count + 1, total: acc.total + (r.total || 0) }),
    { count: 0, total: 0 },
  ), [rows]);

  const isToday = currentDay === fmtDateInput(new Date());
  const shiftDay = (delta) => {
    const d = new Date(currentDay + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setCurrentDay(fmtDateInput(d));
  };
  const dayLabel = (() => {
    try { return new Date(currentDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }); }
    catch (e) { return currentDay; }
  })();

  return (
    <div className="w-full px-4 py-6">
      {/* Header retiré (owner Studio 2026-08-14) — remplacé par le bouton
          déroulant VenteTabs + bascule Ventes/Services, comme /reservations. */}
      <VenteTabs />

      {/* Day navigation */}
      <div className="bg-white rounded-2xl border border-gray-200 p-2 mb-4 flex items-center justify-between gap-2">
        {/* Nav par jour = famille btn-day (Règle Jour). */}
        <button
          onClick={() => shiftDay(-1)}
          aria-label="Jour précédent"
          className="btn-day h-10 w-10 rounded-xl transition-colors"
        >
          <IcChevronLeft />
        </button>
        <div className="min-w-0 text-center">
          <p className="text-sm font-semibold text-gray-700 capitalize truncate">{dayLabel}</p>
        </div>
        <button
          onClick={() => shiftDay(1)}
          disabled={isToday}
          aria-label="Jour suivant"
          className="btn-day h-10 w-10 rounded-xl transition-colors disabled:cursor-not-allowed"
        >
          <IcChevronRight />
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-green-50 text-green-700 flex items-center justify-center"><IcSparkles /></span>
          <div className="min-w-0">
            <p className="text-xs text-gray-400">Services</p>
            <p className="text-lg font-bold text-gray-800">{totals.count}</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
          <span className="h-10 w-10 shrink-0 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center"><IcBanknote /></span>
          <div className="min-w-0">
            <p className="text-xs text-gray-400">Total</p>
            <p className="text-sm font-bold text-green-700 tabular-nums leading-tight">{fmt(totals.total)}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher service / client..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            aria-label="Effacer"
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <IcClose />
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcSparkles className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucun service vendu ce jour.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200">
              <span className="h-12 w-12 shrink-0 rounded-xl bg-green-50 text-green-700 flex items-center justify-center"><IcSparkles /></span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">
                  {r.name}{r.quantity > 1 && <span className="text-gray-400 font-normal"> × {r.quantity}</span>}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1 min-w-0">
                  <IcUser size={10} className="shrink-0" /> <span className="truncate min-w-0">{r.client}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-gray-900">{fmt(r.total)}</p>
                <span className={`bdg ${r.paid ? 'bdg-pos' : 'bdg-critical'}`}>
                  {r.paid ? <IcCheckCircle size={10} /> : <IcAlertCircle size={10} />} {r.paid ? 'Payé' : 'Non payé'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
